import React, { useState } from 'react'
import PartnerSelect from '../../components/admin/PartnerSelect'
import CouponsTable from '../../components/admin/CouponsTable'
import CouponFormModal from '../../components/admin/CouponFormModal'
import { useAdminCoupons, AdminCoupon, CouponFormData, CouponType } from '../../hooks/useAdminCoupons'

// ── Styles ─────────────────────────────────────────────

const styles = {
  card: {
    background: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '0.75rem',
    padding: '2rem',
  } as React.CSSProperties,
  btnPrimary: {
    padding: '0.5rem 1rem',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  errorBanner: {
    padding: '0.75rem 1rem',
    background: '#fef2f2',
    color: '#dc2626',
    borderRadius: '0.375rem',
    fontSize: '0.85rem',
    marginBottom: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  } as React.CSSProperties,
}

// ── Page ───────────────────────────────────────────────

const AdminCouponsPage: React.FC = () => {
  const [partnerId, setPartnerId] = useState<string | null>(null)
  const [couponTypeFilter, setCouponTypeFilter] = useState<CouponType>('PARTNER')
  const [showModal, setShowModal] = useState(false)
  const [editingCoupon, setEditingCoupon] = useState<AdminCoupon | null>(null)

  const {
    coupons,
    loading,
    error,
    clearError,
    createCoupon,
    updateCoupon,
    toggleStatus,
    deleteCoupon,
  } = useAdminCoupons(partnerId, couponTypeFilter)

  // ── Handlers ──

  const handleCreate = () => {
    setEditingCoupon(null)
    setShowModal(true)
  }

  const handleEdit = (coupon: AdminCoupon) => {
    setEditingCoupon(coupon)
    setShowModal(true)
  }

  const handleSubmit = async (form: CouponFormData): Promise<boolean> => {
    if (editingCoupon) {
      return await updateCoupon(editingCoupon.id, form)
    }
    return await createCoupon(form)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingCoupon(null)
  }

  return (
    <div data-testid="admin-coupons-page">
      <h1 style={{ fontSize: '1.5rem', color: '#1e293b', marginBottom: '1.5rem' }}>
        🎟️ Gestion des coupons
      </h1>

      {/* Error banner */}
      {error && (
        <div style={styles.errorBanner} data-testid="coupon-error-banner">
          <span>{error}</span>
          <button
            onClick={clearError}
            style={{
              background: 'none',
              border: 'none',
              color: '#dc2626',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '1.1rem',
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Partner selector */}
      <div style={{ ...styles.card, marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/* Type selector */}
          <div style={{ flex: '0 0 auto' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '0.3rem',
                fontWeight: '600',
                color: '#334155',
                fontSize: '0.9rem',
              }}
            >
              Type de coupon
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                data-testid="type-filter-partner"
                onClick={() => setCouponTypeFilter('PARTNER')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: '1px solid',
                  borderColor: couponTypeFilter === 'PARTNER' ? '#3b82f6' : '#cbd5e1',
                  background: couponTypeFilter === 'PARTNER' ? '#3b82f6' : 'white',
                  color: couponTypeFilter === 'PARTNER' ? 'white' : '#475569',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.9rem',
                }}
              >
                🏢 PARTNER
              </button>
              <button
                data-testid="type-filter-user"
                onClick={() => setCouponTypeFilter('USER')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: '1px solid',
                  borderColor: couponTypeFilter === 'USER' ? '#3b82f6' : '#cbd5e1',
                  background: couponTypeFilter === 'USER' ? '#3b82f6' : 'white',
                  color: couponTypeFilter === 'USER' ? 'white' : '#475569',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.9rem',
                }}
              >
                👤 USER
              </button>
            </div>
          </div>

          {/* Partner selector */}
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '0.3rem',
                fontWeight: '600',
                color: '#334155',
                fontSize: '0.9rem',
              }}
            >
              Partenaire {couponTypeFilter === 'USER' ? '(optionnel)' : ''}
            </label>
            <PartnerSelect value={partnerId} onChange={setPartnerId} />
          </div>
        </div>
      </div>

      {/* Coupons section */}
      {(couponTypeFilter === 'USER' || partnerId) && (
        <div style={styles.card}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.25rem',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '1.15rem', color: '#1e293b' }}>
              {couponTypeFilter === 'PARTNER' ? 'Coupons partenaire' : 'Coupons utilisateur'}
              {partnerId ? ' du partenaire' : ''}
            </h2>
            <button
              style={styles.btnPrimary}
              onClick={handleCreate}
              data-testid="create-coupon-btn"
            >
              + Créer un coupon {couponTypeFilter}
            </button>
          </div>

          <CouponsTable
            coupons={coupons}
            loading={loading}
            onEdit={handleEdit}
            onToggle={toggleStatus}
            onDelete={deleteCoupon}
            couponType={couponTypeFilter}
          />
        </div>
      )}

      {couponTypeFilter === 'PARTNER' && !partnerId && (
        <p style={{ color: '#94a3b8', fontStyle: 'italic' }}>
          Sélectionnez un partenaire pour voir ses coupons PARTNER.
        </p>
      )}

      {/* Modal create/edit */}
      {showModal && (
        <CouponFormModal
          coupon={editingCoupon}
          couponType={couponTypeFilter}
          onSubmit={handleSubmit}
          onClose={handleCloseModal}
          loading={loading}
        />
      )}
    </div>
  )
}

export default AdminCouponsPage
